/**
 * SO-10 city vote, eager and tiny (the manifest's badge and map check): what this device knows
 * about the current vote month, kept in the KV store, and the server clock once the lazy chunk
 * has learned it (clock.tsx). The server is the authority; these only decide whether to show a
 * dot on the rail entry and whether the map should announce a vote.
 */
import type { AppContext } from '../../game/context.js';
import { currentStep } from '../../services/firstSession.js';

/** The vote month the map already announced (`YYYY-MM`). */
export const SEEN_KEY = 'vote.seenMonth';
/** The vote month this player voted in (`YYYY-MM`), so the rail dot goes away. */
export const VOTED_KEY = 'vote.votedMonth';

/** The server's vote month for an instant: the UTC calendar month (apps/api social/votes.ts monthOf). */
export function utcMonth(ms: number): string {
  return new Date(ms).toISOString().slice(0, 7);
}

type Ctx = Pick<AppContext, 'services' | 'session'>;

/** server time − device time, per session (learned from `GET /v1/me` by clock.tsx) */
const offsets = new WeakMap<object, number>();

export function setClockOffset(a: Pick<AppContext, 'session'>, ms: number): void {
  offsets.set(a.session, ms);
}

/** The server clock where this session learned it, else the device clock. */
export function serverNow(a: Ctx): number {
  return a.services.now() + (offsets.get(a.session) ?? 0);
}

export function votedThisMonth(a: Ctx): boolean {
  return a.services.kv.get(VOTED_KEY) === utcMonth(serverNow(a));
}

export function announcedThisMonth(a: Ctx): boolean {
  return a.services.kv.get(SEEN_KEY) === utcMonth(serverNow(a));
}

/** Flow A (first session) is over: no vote card or rail dot before the save prompt. */
export function firstSessionOver(a: Pick<AppContext, 'services'>): boolean {
  return currentStep(a.services.store.state) === 'done';
}
